import { inject, untracked } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { exhaustMap, of, pipe, takeUntil, tap } from 'rxjs';

import { ChargerCommandType, ManualCommandExecutionResponseType } from '@core/api/charger-details/charger-details.enums';
import { CustomOcppExecutionResult, ManualCommandExecutionResponse } from '@core/api/charger-details/charger-details.models';
import { ChargerDetailsService } from '@core/api/charger-details/charger-details.service';
import { ToastService } from '@core/services/toast.service';

import { ToastMessageType } from '@shared/components/toast-message/toast-message.view-model';
import { withCancellation } from '@shared/stores/features/with-cancellation.feature';

interface CustomOcppCommandTarget {
  siteId: string;
  chargerId: string;
}

interface CustomOcppCommandRequest extends CustomOcppCommandTarget {
  payload: string;
}

interface CustomOcppCommandState {
  activeTarget: CustomOcppCommandTarget | undefined;
  executionResult: CustomOcppExecutionResult | undefined;
  isDialogOpen: boolean;
  isLoading: boolean;
  error: string | undefined;
}

const initialState: CustomOcppCommandState = {
  activeTarget: undefined,
  executionResult: undefined,
  isDialogOpen: false,
  isLoading: false,
  error: undefined,
};

export const CustomOcppCommandStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCancellation('clear'),
  withMethods((store) => {
    const chargerDetailsService = inject(ChargerDetailsService);
    const toastService = inject(ToastService);
    const translateService = inject(TranslateService);
    const handleError = (error: string, ocppMessage?: string): void => {
      const message = ocppMessage ? `${error} - OCPP: ${ocppMessage}` : error;
      patchState(store, { isLoading: false, error: message });
      showToast(toastService, ToastMessageType.Error, message);
    };
    const handleResponse = (response: ManualCommandExecutionResponse): void => {
      if (!response.success) {
        handleError(response.message || 'Command execution failed', response.ocppMessage);
        return;
      }
      if (response.type !== ManualCommandExecutionResponseType.CustomOcppCommand) {
        handleError('Unexpected command response');
        return;
      }
      if (response.ocppSuccess === false) {
        const error = response.ocppMessage || 'OCPP command failed';
        patchState(store, { isLoading: false, error });
        showToast(toastService, ToastMessageType.Warning, response.ocppMessage || error);
        return;
      }

      patchState(store, { isLoading: false, executionResult: response.customOcppResult });
      const successMessage = translateService.instant('single-site.charger-details-screen.success-toast.custom-command-success');
      showToast(toastService, ToastMessageType.Success, [successMessage, response.ocppMessage].filter(Boolean).join(' - '));
    };

    const sendCommand = rxMethod<CustomOcppCommandRequest>(
      pipe(
        tap(({ siteId, chargerId }) =>
          patchState(store, {
            activeTarget: { siteId, chargerId },
            executionResult: undefined,
            isLoading: true,
            error: undefined,
          })
        ),
        exhaustMap(({ chargerId, payload }) =>
          chargerDetailsService.sendChargerCommand$(chargerId, { type: ChargerCommandType.CustomOcppCommand, payload }).pipe(
            tapResponse({
              next: handleResponse,
              error: (error: unknown) => handleError(getErrorMessage(error)),
            }),
            takeUntil(store.clear$)
          )
        )
      )
    );

    return {
      send(request: CustomOcppCommandRequest): void {
        if (untracked(() => store.isLoading())) {
          return;
        }
        sendCommand(request);
      },

      reset(): void {
        store.clear();
        patchState(store, initialState);
      },

      openDialog(): boolean {
        if (untracked(() => store.isDialogOpen())) {
          return false;
        }
        patchState(store, { ...initialState, isDialogOpen: true });
        return true;
      },

      closeDialog(): void {
        patchState(store, initialState);
      },
    };
  })
);

const getErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : 'Failed to send charger command');

const showToast = (toastService: ToastService, type: ToastMessageType, message: string): void => {
  toastService.openToast({
    type,
    message$: of(message),
    closeable: true,
    duration: 7000,
  });
};

export type CustomOcppCommandStoreInstance = InstanceType<typeof CustomOcppCommandStore>;
